import { Destination2 } from '~/models/Destination'
import { Itinerary2, TRIP_PHASE, TRIP_TASK_STATE, TripOptions, TripTask } from '~/models/Itinerary'
import { Marker2 } from '~/models/Marker'
import Media from '~/models/Media'
import { ItinerarySpot2 as ItinerarySpot } from '~/models/ItinerarySpot'
import { Page } from '~/models/PaginatedItems'
import { Spot2 } from '~/models/Spot'
import { Traveler2 } from '~/models/Traveler'
import {
	ApiItinerary,
	ApiItineraryDay,
	ApiItineraryGroup,
	ApiItineraryMarker,
	ApiItineraryOptions,
	ApiItinerarySpot,
	ApiProductFilters,
	ApiSpotGroup,
	APITripTask,
	ApiTripTaskAsignees,
} from '~/passporter-services/itinerary/apiTypes'
import { ItineraryEntityMapper } from '~/passporter-services/itinerary/entityMapper'
import { BaseRepository } from '../baseRepository'
import { DestinationMapperEntity } from '../destination/toEntity'
import { ApiMedia } from '../media/apitype'
import { MediaEntityMapper } from '../media/entityMapper'
import { ApiPage, toPage } from '../shared/pagination'
import { ApiSpot } from '../spot/apiTypes'
import { SpotEntityMapper } from '../spot/entityMapper'
import { APITraveler } from '../traveler/apiType'
import { TravelerEntityMapper } from '../traveler/entityMapper'
import { ItineraryApiMapper } from './apiMapper'
import ItineraryGroup from '~/models/ItineraryGroup'
import { ItineraryDay } from '~/models/ItineraryDay'
import Product, { PRODUCT_ORDER, ProductFilters } from '~/models/Product'
import { ProductEntityMapper } from '../product/entityMapper'
import { removeEmptyValues } from '~/models/helpers/removeUndefined'
import { ApiProduct } from '../product/apiType'
import { ProductCache } from '../product/cache'
import { SpotRecommendationsCache } from './spotRecommendationsCache'
import { SpotTagsCache } from './spotTagsCache'
import { TripsCache } from './tripsCache'
import { itineraryDayTimespansCache } from '~/passporter-services/itinerary/cache'

export class ItineraryRepository extends BaseRepository {
	static resource = 'v1/itineraries'
	private static productCache = new ProductCache()
	private static spotRecommendationsCache = new SpotRecommendationsCache()
	private static spotTagsCache = new SpotTagsCache()
	private static tripsCache = new TripsCache()

	static clearCaches(): void {
		this.productCache.clear()
		this.spotRecommendationsCache.clear()
		this.spotTagsCache.clear()
		this.tripsCache.clear()
	}

	static clearTripsCache(): void {
		this.tripsCache.clear()
	}

	static async createItinerary(form: any): Promise<Itinerary2> {
		const response: ApiItinerary | undefined = await this.post(`${this.resource}/`, form)
		this.tripsCache.clear()
		return ItineraryEntityMapper.toItinerary(response!)
	}

	static async editItinerary(itinerary: Itinerary2): Promise<Itinerary2> {
		const apiItinerary = ItineraryApiMapper.toItineraryCreationForm(itinerary)
		const response: ApiItinerary | undefined = await this.patch(
			`${this.resource}/${itinerary.id}/`,
			apiItinerary
		)
		this.tripsCache.clear()
		return ItineraryEntityMapper.toItinerary(response!)
	}

	static async editItineraryCover(
		itineraryId: string,
		mediaId: string | null
	): Promise<Itinerary2> {
		const response: ApiItinerary | undefined = await this.patch(
			`${this.resource}/${itineraryId}/`,
			{
				cover_id: mediaId,
			}
		)
		this.tripsCache.clear()
		return ItineraryEntityMapper.toItinerary(response!)
	}

	static async getMarkers(
		itineraryId: string,
		groupId?: string,
		itineraryMetadata?: boolean
	): Promise<Marker2[]> {
		const params: Record<string, string | boolean> = {}
		if (groupId) params.group_id = groupId
		if (itineraryMetadata) params.itinerary_metadata = true
		const response: ApiItineraryMarker[] = await this.get(
			`${this.resource}/${itineraryId}/markers/`,
			params
		)
		return ItineraryEntityMapper.parseItineraryMarkers(response, itineraryMetadata)
	}

	static async addMedia(itineraryId: string, mediaId: string): Promise<Media> {
		const response: ApiMedia | undefined = await this.post(
			`${this.resource}/${itineraryId}/medias/`,
			{
				media_id: mediaId,
			}
		)
		return MediaEntityMapper.toMedia(response!)
	}

	static async getMedias(itineraryId: string): Promise<Page<Media>> {
		const response: ApiPage<ApiMedia> | undefined = await this.get(
			`${this.resource}/${itineraryId}/medias/`
		)
		const medias = MediaEntityMapper.toMedias(response!.results)
		return toPage(response!, medias)
	}

	static async deleteMedia(itineraryId: string, mediaId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/medias/${mediaId}/`)
	}

	static async getDestinations(itineraryId: string): Promise<Destination2[]> {
		const response: ApiPage<ApiMedia> | undefined = await this.get(
			`${this.resource}/${itineraryId}/destinations/`
		)
		const destinations = DestinationMapperEntity.toDestinations(response!.results)
		return destinations
	}

	static async getItineraryOptions(
		itineraryId: string,
		invitationCode?: string
	): Promise<TripOptions> {
		const response: ApiItineraryOptions = await this.options(`${this.resource}/${itineraryId}/`, {
			params: {
				invitation_code: invitationCode,
			},
		})
		return ItineraryEntityMapper.toTripOptions(response)
	}

	static async getItinerary(itineraryId: string): Promise<Itinerary2> {
		const response: ApiItinerary = await this.get(`${this.resource}/${itineraryId}/`)
		return ItineraryEntityMapper.toItinerary(response)
	}

	static async getInvitationTrip(tripId: string, invitationCode?: string): Promise<Itinerary2> {
		const response: ApiItinerary = await this.get(`${this.resource}/${tripId}/`, {
			invitation_code: invitationCode,
		})
		return ItineraryEntityMapper.toItinerary(response)
	}

	static async getSpots(itineraryId: string, page: number, groupId?: string): Promise<Page<Spot2>> {
		const params: any = { page }
		if (groupId) params.group_id = groupId
		const response: ApiPage<ApiSpot> = await this.get(
			`${this.resource}/${itineraryId}/spots/`,
			params
		)
		const spots: Spot2[] = SpotEntityMapper.toItinerarySpots(response.results)
		return toPage(response, spots)
	}

	static async getSpot(itineraryId: string, spotId: string): Promise<ItinerarySpot> {
		const response: ApiItinerarySpot = await this.get(
			`${this.resource}/${itineraryId}/spots/${spotId}/`
		)
		return SpotEntityMapper.toItinerarySpot(response)
	}

	static async saveSpots(
		itineraryId: string,
		spotIds: string[],
		groupIds?: string[],
		isReference?: boolean,
		collectionId?: string
	): Promise<Spot2[]> {
		const request = ItineraryApiMapper.toItinerarySpots(
			spotIds,
			groupIds,
			isReference,
			collectionId
		)
		const response: Spot2[] | undefined = await this.post(
			`${this.resource}/${itineraryId}/spots/`,
			request
		)
		if (collectionId) this.spotTagsCache.deleteTripTags(itineraryId)
		itineraryDayTimespansCache.invalidateItinerary(itineraryId)
		return SpotEntityMapper.toItinerarySpots(response!)
	}

	static async deleteSpot(itineraryId: string, spotId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/spots/${spotId}/`)
	}

	static async getTravelers(itineraryId: string): Promise<Traveler2[]> {
		const response: ApiPage<APITraveler> = await this.get(
			`${this.resource}/${itineraryId}/travelers/`
		)
		return TravelerEntityMapper.toTravelers(response.results)
	}

	static async deleteTraveler(itineraryId: string, travelerId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/travelers/${travelerId}/`)
		this.tripsCache.clear()
	}

	static async deletePotentialTraveler(itineraryId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/potential_travelers/`)
	}

	static async getGroups(itineraryId: string): Promise<ItineraryGroup[]> {
		const cached = this.spotTagsCache.getTripTags(itineraryId)
		if (cached) return cached

		const response: ApiItineraryGroup[] = await this.get(`${this.resource}/${itineraryId}/groups/`)
		const groups = ItineraryEntityMapper.parseItineraryGroups(response)
		this.spotTagsCache.setTripTags(itineraryId, groups)
		return groups
	}

	static async createGroup(itineraryId: string, group: ItineraryGroup): Promise<ItineraryGroup> {
		const request: ApiSpotGroup = ItineraryApiMapper.toSpotGroup(group)
		const response: ApiItineraryGroup | undefined = await this.post(
			`${this.resource}/${itineraryId}/groups/`,
			request
		)
		const created = ItineraryEntityMapper.parseItineraryGroup(response!)
		this.spotTagsCache.addTag(itineraryId, created)
		return created
	}

	static async editGroup(
		itineraryId: string,
		groupId: string,
		group: ItineraryGroup
	): Promise<ItineraryGroup> {
		const request: ApiSpotGroup = ItineraryApiMapper.toSpotGroup(group)
		const response: ApiItineraryGroup | undefined = await this.patch(
			`${this.resource}/${itineraryId}/groups/${groupId}/`,
			request
		)
		const updated = ItineraryEntityMapper.parseItineraryGroup(response!)
		this.spotTagsCache.updateTag(itineraryId, updated)
		return updated
	}

	static async deleteGroup(itineraryId: string, groupId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/groups/${groupId}/`)
		this.spotTagsCache.removeTag(itineraryId, groupId)
	}

	static async getRecommendedSpots(itineraryId: string): Promise<Spot2[]> {
		const params: Record<string, string | number> = {
			page: 0,
			page_size: 10,
		}

		const response: ApiPage<ApiSpot> = await this.get(
			`${this.resource}/${itineraryId}/spots/endorsements/`,
			{ ...params }
		)
		return SpotEntityMapper.toSpots(response.results)
	}

	static async getMostSavedSpots(
		itineraryId: string,
		page: number = 0,
		pageSize: number = 10
	): Promise<Page<Spot2>> {
		const cached = this.spotRecommendationsCache.getItineraryPage(itineraryId, page, pageSize)
		if (cached) return cached

		const response: ApiPage<ApiSpot> = await this.get(
			`${this.resource}/${itineraryId}/spots/recommendations/`,
			{ page, page_size: pageSize }
		)
		const spots = SpotEntityMapper.toSpots(response.results)
		const result = toPage(response, spots)
		this.spotRecommendationsCache.setItineraryPage(itineraryId, result, page, pageSize)
		return result
	}

	static async createTripTask(itineraryId: string, task: TripTask): Promise<TripTask> {
		const tasks = await this.createMultipleTripTasks(itineraryId, [task], task.state)
		return tasks[0]!
	}

	static async createMultipleTripTasks(
		itineraryId: string,
		tasks: TripTask[],
		state: TRIP_TASK_STATE
	): Promise<TripTask[]> {
		const request: APITripTask[] = tasks.map((task) =>
			ItineraryApiMapper.toTripTask({ ...task, state })
		)
		const response: APITripTask[] | undefined = await this.post(
			`${this.resource}/${itineraryId}/tasks/bulk/`,
			request
		)
		return ItineraryEntityMapper.toTasks(response!)
	}

	static async getTripTask(itineraryId: string, taskId: string): Promise<TripTask> {
		const response: APITripTask | undefined = await this.get(
			`${this.resource}/${itineraryId}/tasks/${taskId}/`
		)
		return ItineraryEntityMapper.toTask(response!)
	}

	static async editTask(
		itineraryId: string,
		taskId: string,
		taskData: Partial<TripTask>
	): Promise<TripTask> {
		const request: APITripTask = ItineraryApiMapper.toTripTask(taskData as TripTask)
		const response: APITripTask | undefined = await this.patch(
			`${this.resource}/${itineraryId}/tasks/${taskId}/`,
			request
		)
		return ItineraryEntityMapper.toTask(response!)
	}

	static async assignTask(
		itineraryId: string,
		taskId: string,
		assignees: Traveler2[]
	): Promise<Traveler2[]> {
		const request: ApiTripTaskAsignees = ItineraryApiMapper.toTripTaskAssignees(assignees)
		const response: APITraveler[] | undefined = await this.post(
			`${this.resource}/${itineraryId}/tasks/${taskId}/assign/`,
			request
		)
		return TravelerEntityMapper.toTravelers(response!)
	}

	static async retrieveTaskAssignees(itineraryId: string, taskId: string): Promise<Traveler2[]> {
		const response: APITraveler[] = await this.get(
			`${this.resource}/${itineraryId}/tasks/${taskId}/asignees/`
		)
		return TravelerEntityMapper.toTravelers(response)
	}

	static async removeTask(itineraryId: string, taskId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/tasks/${taskId}/`)
	}

	static async getTasksFrom(
		itineraryId: string,
		state: TRIP_TASK_STATE,
		page: number
	): Promise<Page<TripTask>> {
		const params: any = { page, page_size: 20, state }
		const response: ApiPage<APITripTask> | undefined = await this.get(
			`${this.resource}/${itineraryId}/tasks/`,
			params
		)
		if (!response?.results) throw Error()
		const tasks = ItineraryEntityMapper.toTasks(response?.results)
		return toPage(response!, tasks)
	}

	static async deleteItinerary(itineraryId: string): Promise<void> {
		const route = `${this.resource}/${itineraryId}/`
		await this.delete(route)
		this.tripsCache.clear()
	}

	static async getDays(itineraryId: string): Promise<ItineraryDay[]> {
		const response: ApiItineraryDay[] = await this.get(`${this.resource}/${itineraryId}/days/`)
		return ItineraryEntityMapper.toDays(response)
	}

	static async updateDay(itineraryId: string, day: Partial<ItineraryDay>): Promise<ItineraryDay> {
		const request = ItineraryApiMapper.toDay(day)
		const response: ApiItineraryDay | undefined = await this.post(
			`${this.resource}/${itineraryId}/days/`,
			request
		)
		return ItineraryEntityMapper.toDay(response!)
	}

	static async getDaySpots(itineraryId: string, day: number): Promise<Spot2[]> {
		const response: ApiSpot[] = await this.get(`${this.resource}/${itineraryId}/days/${day}/spots/`)
		return SpotEntityMapper.toSpots(response)
	}

	static async addSpotsToDay(
		itineraryId: string,
		day: number,
		spotIds: string[],
		timespanId?: string,
		order?: number
	): Promise<Spot2[]> {
		const request: Record<string, unknown> = { spot_ids: spotIds }
		if (timespanId) request.timespan_id = timespanId
		if (order) request.order = order
		const response: ApiSpot[] | undefined = await this.post(
			`${this.resource}/${itineraryId}/days/${day}/spots/`,
			request
		)
		itineraryDayTimespansCache.invalidateDay(itineraryId, day)
		return SpotEntityMapper.toSpots(response!)
	}

	static async updateSpotInDay(
		itineraryId: string,
		day: number,
		spotId: string,
		timespanId?: string,
		order?: number
	): Promise<Spot2> {
		const request: Record<string, unknown> = { timespan_id: timespanId ?? null }
		if (order) request.order = order
		const response: ApiSpot | undefined = await this.patch(
			`${this.resource}/${itineraryId}/days/${day}/spots/${spotId}/`,
			request
		)
		itineraryDayTimespansCache.invalidateDay(itineraryId, day)
		return SpotEntityMapper.toSpot(response!)
	}

	static async deleteSpotFromDay(itineraryId: string, day: number, spotId: string): Promise<void> {
		await this.delete(`${this.resource}/${itineraryId}/days/${day}/spots/${spotId}/`)
		itineraryDayTimespansCache.invalidateDay(itineraryId, day)
	}

	static async orderSpotFromDay(
		itineraryId: string,
		day: number,
		spotId: string,
		order: number
	): Promise<Spot2[]> {
		const response: ApiSpot[] | undefined = await this.post(
			`${this.resource}/${itineraryId}/days/${day}/spots/${spotId}/order/`,
			{ order }
		)
		return SpotEntityMapper.toSpots(response!)
	}

	static async updatedDay(
		itineraryId: string,
		itineraryDay: Partial<ItineraryDay>
	): Promise<ItineraryDay> {
		const data = ItineraryApiMapper.toDay(itineraryDay)
		const response: ApiItineraryDay | undefined = await this.patch(
			`${this.resource}/${itineraryId}/days/`,
			data
		)
		return ItineraryEntityMapper.toDay(response!)
	}

	static async getDayMarkers(itineraryId: string, dayNumbers?: number[]): Promise<Marker2[]> {
		let params = undefined
		if (dayNumbers?.length) params = { day_numbers: dayNumbers.join() }
		const response: ApiItineraryMarker[] | undefined = await this.get(
			`${this.resource}/${itineraryId}/days/markers/`,
			params
		)
		return ItineraryEntityMapper.parseDayMarkers(response!)
	}

	static async getTasksCountByState(tripId: string): Promise<Record<string, number>> {
		const response: Record<string, number> = await this.get(
			`${this.resource}/${tripId}/tasks/count/`
		)
		return response
	}

	static async getTrips(
		phase: TRIP_PHASE,
		userId: string,
		order: string,
		page: number,
		pageSize: number
	): Promise<Page<Itinerary2>> {
		const cached = this.tripsCache.getTripsPage(phase, userId, order, page, pageSize)
		if (cached) return cached

		const phaseParam = ItineraryApiMapper.toPhase(phase)
		const params: Record<string, string | number> = {
			user_id: userId,
			order,
			page,
			page_size: pageSize,
		}
		if (phaseParam) params.phase = phaseParam
		const response: ApiPage<ApiItinerary> = await this.get(`${this.resource}/`, params)
		const itineraries = ItineraryEntityMapper.toItineraries(response!.results)
		const result = toPage(response!, itineraries)
		this.tripsCache.setTripsPage(phase, userId, order, result, page, pageSize)
		return result
	}

	static async getProducts(
		tripId: string,
		order: PRODUCT_ORDER,
		page: number = 0,
		pageSize: number = 10,
		filters: ProductFilters
	): Promise<Page<Product>> {
		const mapFilters: ApiProductFilters = {
			nights: filters?.nights,
			min_budget: filters?.range?.min,
			max_budget: filters?.range?.max,
			order: order,
			only_favorites: filters?.favorites,
			category: filters.category as any,
			language: filters.language,
			accessibility_wheelchair: filters.accessibilityWheelchair || undefined,
			free_cancellation_policy: filters.freeCancellation || undefined,
		}
		const cleanFilters = removeEmptyValues(mapFilters)

		const cached = this.productCache.getTripPage(tripId, page, pageSize, cleanFilters)
		if (cached) return cached

		const response: ApiPage<ApiProduct> = await this.get(`${this.resource}/${tripId}/products/`, {
			...cleanFilters,
			page,
			page_size: pageSize,
		})

		const products = ProductEntityMapper.toProducts(response.results)
		const result = toPage(response, products)
		this.productCache.setTripPage(tripId, result, page, pageSize, cleanFilters)
		return result
	}

	static async saveProduct(itineraryId: string, productId: string): Promise<void> {
		this.productCache.deleteTripPages(itineraryId)
		await this.post(`${this.resource}/${itineraryId}/products/${productId}/favorites/`)
	}

	static async removeProduct(itineraryId: string, productId: string): Promise<void> {
		this.productCache.deleteTripPages(itineraryId)
		await this.delete(`${this.resource}/${itineraryId}/products/${productId}/favorites/`)
	}
}
